import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import moment from "moment";
import "moment/locale/ru";
import cn from "classnames";
import style from "./CurrentLocationBlock.module.scss";
import { Order } from "../../../../../services";
import CustomInput from "../../../../custom/custom-input/CustomInput";
import CustomButton from "../../../../custom/custom-button/CustomButton";
import ChevronIcon from "../../../../shared/icons/ChevronIcon";
import { useTranslation } from "react-i18next";

const SHOW_STATUSES = ["onthetrip"];

const formatPointDate = (iso, emptyValue) => {
  if (!iso) return emptyValue;
  return moment(iso).format("DD.MM.YYYY (HH:mm)");
};

const splitLocation = (location) => {
  if (!location) return { country: "", rest: "" };
  const [country, ...rest] = location.split(",");
  return { country: country.trim(), rest: rest.join(",").trim() };
};

const CurrentLocationBlock = ({ order }) => {
  const { t } = useTranslation();
  const orderId = order?.id;
  const status = (order?.status || "").toLowerCase();

  const [points, setPoints] = useState([]);
  const [location, setLocation] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const fetchPoints = useCallback(() => {
    if (!orderId) return;
    Order.getOrderTrackingPoints(orderId)
      .then((r) => setPoints(Array.isArray(r?.data) ? r.data : []))
      .catch(() => setPoints([]));
  }, [orderId]);

  useEffect(() => {
    fetchPoints();
  }, [fetchPoints]);

  const sortedPoints = useMemo(
    () => [...points].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)),
    [points],
  );

  const handleSave = async () => {
    const value = location.trim();
    if (!value) {
      toast.error(t("orderInfoExpeditor.currentLocation.required"));
      return;
    }
    setIsSaving(true);
    try {
      await Order.createOrderTrackingPoint(orderId, value);
      toast.success(t("orderInfoExpeditor.currentLocation.saved"));
      setLocation("");
      fetchPoints();
    } catch {
      toast.error(t("orderInfoExpeditor.currentLocation.saveError"));
    } finally {
      setIsSaving(false);
    }
  };

  if (!SHOW_STATUSES.includes(status)) return null;

  return (
    <div className={style.card}>
      <div className={style.topbar}>
        <span className={style.topbar_title}>{t("orderInfoExpeditor.currentLocation.title")}</span>
      </div>

      <div className={style.form_row}>
        <div className={style.form_input}>
          <CustomInput
            value={location}
            isBgGray
            labelText={t("orderInfoExpeditor.currentLocation.label")}
            placeholder={t("orderInfoExpeditor.currentLocation.placeholder")}
            onChangeValue={setLocation}
            onKeyPress={(e) => {
              if (e.key === "Enter" && !isSaving) handleSave();
            }}
          />
        </div>
        <div className={style.form_btn}>
          <CustomButton
            text={t("common.save")}
            isNavy
            onClick={handleSave}
            isLoading={isSaving}
            disabled={isSaving}
            style={{ width: "100%" }}
          />
        </div>
      </div>

      <div className={style.history}>
        <button
          type="button"
          className={cn(style.history_head, { [style.history_head_open]: isExpanded })}
          onClick={() => setIsExpanded((prev) => !prev)}
        >
          <span className={style.history_head_link}>
            {t("orderInfoExpeditor.currentLocation.openHistory", { count: sortedPoints.length })}
          </span>
          <ChevronIcon
            className={cn(style.history_chevron, { [style.history_chevron_open]: isExpanded })}
          />
        </button>

        {isExpanded && (
          <div className={style.history_table}>
            <div className={style.history_header}>
              <span className={style.history_col_date}>{t("orderInfoExpeditor.currentLocation.date")}</span>
              <span className={style.history_col_location}>{t("orderInfoExpeditor.currentLocation.location")}</span>
            </div>

            {sortedPoints.length === 0 ? (
              <div className={style.history_empty}>{t("orderInfoExpeditor.currentLocation.emptyHistory")}</div>
            ) : (
              sortedPoints.map((point) => {
                const { country, rest } = splitLocation(point.location);
                return (
                  <div key={point.id} className={style.history_row}>
                    <span className={style.history_col_date}>
                      {formatPointDate(point.createdAt, t("common.emptyValue"))}
                    </span>
                    <span className={style.history_col_location}>
                      <span className={style.history_country}>{country}</span>
                      {rest && <span className={style.history_rest}>, {rest}</span>}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CurrentLocationBlock;
